// src/cockpit4/components/WebhookConsolePanel.tsx
// ============================================
// Webhook 与事件系统（设置中心 P1-功能6 深度扩展）
// 事件订阅（事件→端点）/ Webhook 推送 / 自定义事件 / 事件日志 /
// 失败重试 / HMAC 签名验证（热插拔，无需重启）
// 投递通过后端 /vault-api?action=webhook-test 真实 HTTP 发送（带签名）
// ============================================

import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Webhook, Plus, Trash2, Play, Check, X, Loader2, History, Zap,
} from "lucide-react";
import { integrationConfig, EVENT_CATALOG } from "../../services/integrationConfig";
import type { WebhookSubscription, WebhookLogEntry } from "../../services/integrationConfig";

const CATEGORY_COLORS: Record<string, string> = {
  内容: "#38BDF8", 客户: "#F472B6", 财务: "#FBBF24", 工作流: "#A78BFA",
  知识: "#34D399", Hermes: "#FB923C", 系统: "#F87171", 自定义: "#94A3B8",
};

export default function WebhookConsolePanel() {
  const [subs, setSubs] = useState<WebhookSubscription[]>(integrationConfig.getWebhookSubscriptions());
  const [logs, setLogs] = useState<WebhookLogEntry[]>(integrationConfig.getWebhookLogs());
  const [showForm, setShowForm] = useState(false);
  const [event, setEvent] = useState("content.published");
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [maxRetries, setMaxRetries] = useState(3);
  const [testing, setTesting] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    const unsub = integrationConfig.subscribe(() => {
      setSubs(integrationConfig.getWebhookSubscriptions());
      setLogs(integrationConfig.getWebhookLogs());
    });
    return unsub;
  }, []);

  const stats = useMemo(() => {
    const ok = logs.filter((l) => l.status === "success").length;
    const fail = logs.filter((l) => l.status === "failed").length;
    return { ok, fail, total: logs.length, active: subs.filter((s) => s.enabled).length };
  }, [logs, subs]);

  const addSub = () => {
    if (!url.trim() || !url.startsWith("http")) {
      setFlash("请填写有效的 Webhook URL（http/https 开头）");
      setTimeout(() => setFlash(null), 3000);
      return;
    }
    integrationConfig.addWebhookSubscription(event, url.trim(), secret.trim(), maxRetries);
    setUrl("");
    setSecret("");
    setShowForm(false);
    setFlash("Webhook 订阅已创建（热插拔生效）");
    setTimeout(() => setFlash(null), 3000);
  };

  const testDelivery = async (s: WebhookSubscription) => {
    setTesting(s.id);
    const t0 = Date.now();
    try {
      const resp = await fetch(`/vault-api?action=webhook-test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: s.url,
          secret: s.secret,
          event: s.event,
          payload: { source: "xeaos-webhook-console", event: s.event, time: new Date().toISOString(), test: true },
        }),
      });
      const json = await resp.json().catch(() => ({}));
      const duration = Date.now() - t0;
      const ok = json?.success === true || (json?.statusCode && json.statusCode < 400);
      integrationConfig.logWebhookDelivery({
        event: s.event, url: s.url, status: ok ? "success" : "failed", attempts: 1,
        statusCode: json?.statusCode ?? resp.status,
        response: ok ? (json?.body || "OK") : (json?.error || json?.body || "发送失败"),
        durationMs: duration,
      });
      setFlash(ok ? `投递成功（${duration}ms）` : `投递失败：${json?.error || "请检查 URL"}`);
      setTimeout(() => setFlash(null), 4000);
    } catch (err: any) {
      integrationConfig.logWebhookDelivery({
        event: s.event, url: s.url, status: "failed", attempts: 1,
        statusCode: 0, response: String(err?.message || err), durationMs: Date.now() - t0,
      });
      setFlash(`投递失败：${err?.message || "网络错误"}`);
      setTimeout(() => setFlash(null), 4000);
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Webhook size={14} className="text-indigo-400" />
        <h3 className="text-[12px] font-medium text-white">Webhook 与事件系统</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P1-功能6 · Events</span>
        <div className="ml-auto flex items-center gap-1 text-[9px] font-mono text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> 热插拔已启用
        </div>
      </div>

      {flash && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-lg border border-indigo-500/30 bg-indigo-500/10 p-2 text-[10px] text-indigo-300">
          {flash}
        </motion.div>
      )}

      <div className="grid grid-cols-4 gap-2">
        {[
          { label: "订阅", value: stats.active, color: "#38BDF8", icon: Zap },
          { label: "成功投递", value: stats.ok, color: "#34D399", icon: Check },
          { label: "失败投递", value: stats.fail, color: "#F87171", icon: X },
          { label: "日志总数", value: stats.total, color: "#A78BFA", icon: History },
        ].map((m) => (
          <div key={m.label} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
            <div className="flex items-center gap-1 text-[9px] font-mono text-gray-500 uppercase">
              <m.icon size={10} style={{ color: m.color }} /> {m.label}
            </div>
            <div className="text-lg font-semibold mt-0.5" style={{ color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-lg border border-indigo-500/25 bg-indigo-500/[0.05] p-3">
          <div className="text-[10px] font-mono text-gray-300 uppercase tracking-wider mb-2">新建 Webhook 订阅</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="text-[9px] font-mono text-gray-500">事件类型</label>
              <select value={event} onChange={(e) => setEvent(e.target.value)}
                className="mt-0.5 w-full px-2 py-1.5 rounded-md bg-black/30 border border-white/[0.08] text-[11px] text-gray-300 outline-none focus:border-indigo-500/40">
                {EVENT_CATALOG.map((e) => (
                  <option key={e.id} value={e.id}>{e.label}（{e.id}）</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-[9px] font-mono text-gray-500">目标 URL</label>
              <input value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://your-server.com/hooks/xeaos"
                className="mt-0.5 w-full px-2 py-1.5 rounded-md bg-black/30 border border-white/[0.08] text-[11px] text-white placeholder-gray-600 outline-none focus:border-indigo-500/40" />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500">签名密钥（HMAC）</label>
              <input value={secret} onChange={(e) => setSecret(e.target.value)}
                placeholder="留空则无签名"
                className="mt-0.5 w-full px-2 py-1.5 rounded-md bg-black/30 border border-white/[0.08] text-[11px] text-white placeholder-gray-600 outline-none focus:border-indigo-500/40" />
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500">失败重试次数</label>
              <select value={maxRetries} onChange={(e) => setMaxRetries(Number(e.target.value))}
                className="mt-0.5 w-full px-2 py-1.5 rounded-md bg-black/30 border border-white/[0.08] text-[11px] text-gray-300 outline-none">
                {[0, 1, 3, 5].map((n) => <option key={n} value={n}>{n} 次</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={addSub}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-500/20 border border-indigo-500/30 text-[10px] text-indigo-300 hover:bg-indigo-500/30">
              <Plus size={10} /> 创建订阅
            </button>
            <button onClick={() => setShowForm(false)}
              className="px-3 py-1.5 rounded-md border border-white/[0.08] text-[10px] text-gray-400 hover:text-white">取消</button>
          </div>
        </motion.div>
      )}

      {subs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/[0.08] p-4 text-center text-[10px] text-gray-500">
          暂无 Webhook 订阅，点击「新建订阅」绑定事件
        </div>
      ) : (
        subs.map((s) => {
          const evt = EVENT_CATALOG.find((e) => e.id === s.event);
          const color = CATEGORY_COLORS[evt?.category || "自定义"] || "#94A3B8";
          return (
            <div key={s.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
              <div className="flex items-center gap-2">
                <span className="px-1.5 py-0.5 rounded text-[9px] font-mono" style={{ background: `${color}22`, color }}>{evt?.label || s.event}</span>
                <code className="font-mono text-[10px] text-gray-300 truncate">{s.url}</code>
                <span className="text-[9px] font-mono text-gray-600">重试 {s.maxRetries} 次</span>
                <button onClick={() => testDelivery(s)} disabled={testing === s.id}
                  className="ml-auto flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/25 text-[9px] text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50">
                  {testing === s.id ? <Loader2 size={9} className="animate-spin" /> : <Play size={9} />} 测试
                </button>
                <button onClick={() => integrationConfig.toggleWebhookSubscription(s.id, !s.enabled)}
                  className={`px-2 py-1 rounded-md text-[9px] border ${s.enabled ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-300" : "bg-gray-500/10 border-gray-500/25 text-gray-400"}`}>
                  {s.enabled ? "已启用" : "已暂停"}
                </button>
                <button onClick={() => integrationConfig.removeWebhookSubscription(s.id)} className="text-gray-500 hover:text-red-400">
                  <Trash2 size={11} />
                </button>
              </div>
              {s.secret && (
                <div className="mt-1 flex items-center gap-1 text-[9px] font-mono text-amber-500/80">
                  <Zap size={9} /> HMAC 签名已启用 · secret={s.secret.slice(0, 6)}…
                </div>
              )}
            </div>
          );
        })
      )}

      {/* 事件目录 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Zap size={11} className="text-sky-400" />
          <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">可订阅事件 · 共 {EVENT_CATALOG.length} 种</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
          {EVENT_CATALOG.map((e) => (
            <div key={e.id} className="rounded-md border border-white/[0.04] bg-black/20 px-2 py-1.5">
              <div className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full" style={{ background: CATEGORY_COLORS[e.category] || "#888" }} />
                <span className="text-[10px] text-gray-300">{e.label}</span>
                <code className="ml-auto font-mono text-[8px] text-gray-600">{e.id}</code>
              </div>
              <div className="mt-0.5 text-[9px] text-gray-600">{e.description}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 投递日志 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center mb-2">
          <History size={11} className="text-purple-400" />
          <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider ml-1.5">投递日志</span>
          <button onClick={() => integrationConfig.clearWebhookLogs()}
            className="ml-auto text-[9px] text-gray-500 hover:text-red-400">清空日志</button>
        </div>
        {logs.length === 0 ? (
          <div className="py-2 text-center text-[10px] text-gray-600">暂无投递记录，点击订阅上的「测试」发起真实投递</div>
        ) : (
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {logs.map((l) => (
              <div key={l.id} className="flex items-center gap-2 px-2 py-1 rounded-md bg-black/20">
                <span className={`w-1.5 h-1.5 rounded-full ${l.status === "success" ? "bg-emerald-400" : "bg-red-400"}`} />
                <code className="font-mono text-[9px] text-gray-400">{l.event}</code>
                <span className="text-[9px] text-gray-600 truncate">{l.response || l.url}</span>
                <span className="ml-auto text-[9px] font-mono text-gray-600">
                  {l.statusCode ? `${l.statusCode}` : ""} · {l.durationMs}ms
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


